import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { suppliersService } from '../services/suppliers.service';
import type { SuppliersListResponse } from '../services/suppliers.service';
import { getQueryState, type QueryWithState } from './useQueryWithState';

export const useSuppliers = (params: URLSearchParams): QueryWithState<SuppliersListResponse> => {
  const query = useQuery({
    queryKey: ['suppliers', params.toString()],
    queryFn: () => suppliersService.getList(params),
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    renderState: getQueryState(query, (data) => data.data?.length === 0),
  };
};

export const useSupplier = (id: string | number, enabled = true): QueryWithState<unknown> => {
  const query = useQuery({
    queryKey: ['suppliers', id],
    queryFn: () => suppliersService.getById(id),
    enabled,
  });

  return {
    ...query,
    renderState: getQueryState(query),
  };
};

export const useSaveSupplier = () =>
  {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, payload }: { id: string | number | null; payload: Record<string, unknown> }) =>
        suppliersService.save(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      },
    });
  };

export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => suppliersService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
};
